import { Box, IconButton, Typography, Button, Stack, Grid2 as Grid } from '@mui/material';
import { useFieldArray } from 'react-hook-form';
import { useEffect } from 'react';
import { RiDeleteBin6Line } from 'react-icons/ri';
import Input from '@/components/Common/Input';
import Select from '@/components/Common/Select';

interface CalComFormProps {
  namePrefix: string;
  control: any;
  getValues: any;
  disabled?: boolean;
  configFields: any;
  spacing: string;
  submitForm?: any;
}

const CalComForm = ({ namePrefix, control, disabled, configFields, getValues }: CalComFormProps) => {
  const providersField = configFields?.find((field: any) => field.key === 'providers');
  const servicesField = providersField?.group?.find((field: any) => field.key === 'services');
  const eventTypeField = servicesField?.group?.find((field: any) => field.key === 'id');
  const eventTypesField = servicesField?.group?.find((field: any) => field.key === 'event_types');
  const eventTypeOptions = (eventTypeField?.options || []).map((option: any) => ({
    label: option.duration ? `${option.label} (${option.duration} min)` : option.label,
    value: option.value
  }));

  const {
    fields: providers,
    append,
    remove
  } = useFieldArray({
    control,
    name: `${namePrefix}.providers`
  });

  useEffect(() => {
    const existingProviders = getValues(`${namePrefix}.providers`);

    if (!existingProviders || existingProviders.length === 0) {
      append({
        name: '',
        services: [{ id: '', name: '', description: '', event_types: [] }]
      });
    }
  }, [append, getValues, namePrefix]);

  return (
    <Box>
      <Typography fontSize={16} fontWeight={600} my={2}>
        Provider Details
      </Typography>

      {providers.map((provider: any, index: number) => (
        <ProviderItem
          key={provider.id}
          index={index}
          control={control}
          remove={remove}
          disabled={disabled}
          namePrefix={namePrefix}
          totalProviders={providers.length}
          eventTypeOptions={eventTypeOptions}
          eventTypesField={eventTypesField}
        />
      ))}

      <Box display='flex' justifyContent='center'>
        <Button
          variant='contained'
          disabled={disabled}
          onClick={() =>
            append({
              name: '',
              services: [{ id: '', name: '', description: '', event_types: [] }]
            })
          }
        >
          + Add Another Provider
        </Button>
      </Box>
    </Box>
  );
};

export default CalComForm;

const ProviderItem = ({
  index,
  control,
  remove,
  disabled,
  namePrefix,
  totalProviders,
  eventTypeOptions,
  eventTypesField
}: any) => {
  const providerPrefix = `${namePrefix}.providers.${index}`;

  const {
    fields: services,
    append: addService,
    remove: removeService
  } = useFieldArray({
    control,
    name: `${providerPrefix}.services`
  });

  useEffect(() => {
    if (services.length === 0) {
      addService({ id: '', name: '', description: '', event_types: [] });
    }
  }, [services.length, addService]);

  return (
    <Box p={3} mb={3} border='1px solid #E5E7EB' borderRadius='12px'>
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography fontWeight={600}>Provider #{index + 1}</Typography>
        {totalProviders > 1 && !disabled && (
          <IconButton color='error' onClick={() => remove(index)}>
            <RiDeleteBin6Line />
          </IconButton>
        )}
      </Stack>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Input
            name={`${providerPrefix}.name`}
            control={control}
            label='Provider Name'
            placeholder='Enter Provider Name'
            disabled={disabled}
            fullWidth
          />
        </Grid>
      </Grid>

      <Box mt={3}>
        <Box display='flex' justifyContent='space-between' mb={2}>
          <Typography fontWeight={500}>Event Types</Typography>

          <Button
            disabled={disabled}
            size='small'
            variant='contained'
            onClick={() => addService({ id: '', name: '', description: '', event_types: [] })}
          >
            Add Event Type
          </Button>
        </Box>

        {services.map((service: any, sIndex: number) => {
          const servicePrefix = `${providerPrefix}.services.${sIndex}`;

          return (
            <Grid container spacing={2} alignItems='center' mb={2} key={service.id}>
              <Grid size={{ xs: 12, md: 3 }}>
                <Select
                  label='Event Type'
                  name={`${servicePrefix}.id`}
                  control={control}
                  disabled={disabled}
                  options={eventTypeOptions}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2.5 }}>
                <Input
                  name={`${servicePrefix}.name`}
                  control={control}
                  label='Name'
                  placeholder='Enter name'
                  disabled={disabled}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2.5 }}>
                <Input
                  name={`${servicePrefix}.description`}
                  control={control}
                  label='Description'
                  placeholder='Enter description'
                  disabled={disabled}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <Select
                  label={eventTypesField?.label || 'Modes'}
                  name={`${servicePrefix}.event_types`}
                  control={control}
                  disabled={disabled}
                  options={eventTypesField?.options || []}
                  multiple
                  fullWidth
                />
              </Grid>

              {services.length > 1 && !disabled && (
                <Grid size='auto'>
                  <IconButton color='error' onClick={() => removeService(sIndex)}>
                    <RiDeleteBin6Line />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          );
        })}
      </Box>
    </Box>
  );
};
